import { useCallback, useEffect, useRef, useState } from 'react';
import { useSignalRContext } from '../Contexts/signalrContext';
import {
	ChatBroadcastType,
	ChatBroadcastWrapper,
	ChatMessage,
	ChatRoomEvent,
	ChatSignalREventType,
	ChatUser,
} from '../Types/chat';
import { transformDateStringPropsToDate } from '../Utils/dateUtils';
import { User } from '../Types/user';
import { ChatNotDeliveredMessagesCount, ChatRoom } from '../Types/admin/chat';
import { useClearAvailableChatRoomsCache } from './admin/useChat';
import { useOptionalEventContext } from '../Contexts/eventContext';

const SKIP_JOIN_LEAVE_EVENTS = true; //might be something we want in the future
const CHAT_ACTIVITY_EVENT_TYPE = 'chatActivity';
const JOIN_RETRY_DELAYS_MS = [1000, 2000, 5000, 10000, 30000];

type ChatActivityEventDetail = {
	action: 'set-room-activity' | 'clear-room-activity' | 'current-room-changed';
	chatRoomId?: number | null;
};

const skipAddMessage = (msg: ChatMessage, currentUser?: User) => {
	if (
		SKIP_JOIN_LEAVE_EVENTS &&
		(msg.EventType === ChatSignalREventType.UserJoined || msg.EventType === ChatSignalREventType.UserLeft)
	) {
		return true;
	}

	if (!currentUser) return false;

	const isFromCurrentUser = msg.FromUserId === currentUser.Id;
	const isJoinLeaveEvent = msg.EventType === ChatSignalREventType.UserJoined || msg.EventType === ChatSignalREventType.UserLeft;
	return isFromCurrentUser && isJoinLeaveEvent;
};

const appendUniqueMessages = (current: ChatMessage[], incoming: ChatMessage[]) => {
	const seenPersistedIds = new Set(current.filter((message) => message.Id > 0).map((message) => message.Id));
	const uniqueIncoming = incoming.filter((message) => {
		if (message.Id <= 0) return true;
		if (seenPersistedIds.has(message.Id)) return false;

		seenPersistedIds.add(message.Id);
		return true;
	});
	return uniqueIncoming.length ? [...current, ...uniqueIncoming] : current;
};

export const useSignalRChat = (
	user?: User,
	loadedChatRooms?: ChatRoom[],
	notDeliveredMessagesCounts?: ChatNotDeliveredMessagesCount[]
) => {
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [currentRoomUsers, setCurrentRoomUsers] = useState<ChatUser[]>([]);
	const [currentRoomId, setCurrentRoomId] = useState<number | null>(null);
	const currentRoomIdRef = useRef<number | null>(null);
	const pendingJoinRoomIdRef = useRef<number | null>(null);
	const pendingLeaveRoomIdsRef = useRef<Set<number>>(new Set());
	const joinRetryTimerRef = useRef<number | null>(null);
	const joinRetryAttemptRef = useRef(0);
	const [roomHistoryLoaded, setRoomHistoryLoaded] = useState(false);
	const [isResynchronizing, setIsResynchronizing] = useState(false);
	const [roomUsersByRoomId, setRoomUsersByRoomId] = useState<Record<number, ChatUser[]>>({});
	const [roomsState, setRoomsState] = useState<Record<number, { hasMoreMessages: boolean }>>({});
	const { subscribe, unsubscribe, sendMessage, connectionState } = useSignalRContext();
	const connectionStateRef = useRef(connectionState);
	connectionStateRef.current = connectionState;
	const isConnected = connectionState === 'CONNECTED';
	const isRoomReady = isConnected && roomHistoryLoaded && !isResynchronizing && currentRoomId !== null;
	const clearAvailableChatRoomsCache = useClearAvailableChatRoomsCache();
	const eventContext = useOptionalEventContext();
	const dispatchBusEvent = eventContext?.dispatch;

	const dispatchChatActivityEvent = useCallback(
		(detail: ChatActivityEventDetail) => {
			dispatchBusEvent?.(CHAT_ACTIVITY_EVENT_TYPE, detail);
		},
		[dispatchBusEvent]
	);

	const sendToHub = useCallback(
		(message: unknown) => {
			sendMessage('CHAT', message);
		},
		[sendMessage]
	);

	const clearJoinRetry = useCallback(() => {
		if (joinRetryTimerRef.current !== null) {
			window.clearTimeout(joinRetryTimerRef.current);
			joinRetryTimerRef.current = null;
		}
		joinRetryAttemptRef.current = 0;
	}, []);

	const sendMessagesDelivered = useCallback(
		(messageIds: number[]) => {
			const persistedMessageIds = messageIds.filter((messageId) => messageId > 0);
			if (!persistedMessageIds.length || connectionStateRef.current !== 'CONNECTED') return;

			sendToHub({
				EventType: ChatSignalREventType.MessagesDelivered,
				MessageIds: persistedMessageIds,
			});
		},
		[sendToHub]
	);

	useEffect(() => {
		if (!notDeliveredMessagesCounts?.length) return;

		setRoomsState((prev) => {
			let hasChanges = false;
			const next = { ...prev };

			notDeliveredMessagesCounts.forEach((roomCount) => {
				if (roomCount.NewMessagesCount <= 0 || roomCount.ChatRoomId === currentRoomIdRef.current) return;

				if (!next[roomCount.ChatRoomId]?.hasMoreMessages) {
					next[roomCount.ChatRoomId] = {
						...next[roomCount.ChatRoomId],
						hasMoreMessages: true,
					};
					hasChanges = true;
				}
			});

			return hasChanges ? next : prev;
		});
	}, [notDeliveredMessagesCounts]);

	useEffect(() => {
		const handleChatMessage = (content: unknown) => {
			try {
				const chatMsgWrapper = content as ChatBroadcastWrapper;
				if (chatMsgWrapper.BroadcastType === ChatBroadcastType.ChatEvent) {
					const chatMessage = transformDateStringPropsToDate(chatMsgWrapper.Data as ChatMessage, ['SentDateUTC']);
					const activeRoomId = currentRoomIdRef.current;

					if (chatMessage.ChatRoomId !== activeRoomId) {
						console.debug('[Chat SignalR] Ignoring live message for an inactive room.', {
							messageRoomId: chatMessage.ChatRoomId,
							activeRoomId,
							messageId: chatMessage.Id,
						});
						if (chatMessage.EventType === ChatSignalREventType.MessageToRoom) {
							setRoomsState((prev) => ({
								...prev,
								[chatMessage.ChatRoomId]: { hasMoreMessages: true },
							}));
							dispatchChatActivityEvent({ action: 'set-room-activity', chatRoomId: chatMessage.ChatRoomId });
						}
						return;
					}

					sendMessagesDelivered([chatMessage.Id]);
					dispatchChatActivityEvent({ action: 'clear-room-activity', chatRoomId: chatMessage.ChatRoomId });
					if (skipAddMessage(chatMessage, user)) return;

					setMessages((prev) => appendUniqueMessages(prev, [chatMessage]));
				} else if (chatMsgWrapper.BroadcastType === ChatBroadcastType.AllMessages) {
					const responseRoomId = chatMsgWrapper.RoomId;
					const activeRoomId = currentRoomIdRef.current;
					const pendingJoinRoomId = pendingJoinRoomIdRef.current;

					if (
						typeof responseRoomId !== 'number' ||
						responseRoomId !== activeRoomId ||
						responseRoomId !== pendingJoinRoomId ||
						loadedChatRooms?.find((room) => room.Id === responseRoomId)?.IsActive === false
					) {
						console.warn('[Chat SignalR] Ignoring stale or uncorrelated history response.', {
							responseRoomId,
							activeRoomId,
							pendingJoinRoomId,
						});
						return;
					}

					const chatMessages = transformDateStringPropsToDate(chatMsgWrapper.Data as ChatMessage[], ['SentDateUTC']);
					if (chatMessages.some((message) => message.ChatRoomId !== responseRoomId)) {
						console.warn('[Chat SignalR] Ignoring history response containing messages from another room.', {
							responseRoomId,
						});
						return;
					}

					clearJoinRetry();
					pendingJoinRoomIdRef.current = null;
					console.info('[Chat SignalR] Room synchronization completed.', {
						roomId: responseRoomId,
						messageCount: chatMessages.length,
					});
					sendMessagesDelivered(chatMessages.map((message) => message.Id));
					dispatchChatActivityEvent({ action: 'clear-room-activity', chatRoomId: responseRoomId });
					setMessages((prev) => appendUniqueMessages(prev, chatMessages));
					setRoomHistoryLoaded(true);
					setIsResynchronizing(false);
					setRoomsState((prev) => ({ ...prev, [responseRoomId]: { hasMoreMessages: false } }));
				} else if (chatMsgWrapper.BroadcastType === ChatBroadcastType.UserList) {
					if (chatMsgWrapper.RoomId !== currentRoomIdRef.current) {
						console.debug('[Chat SignalR] Ignoring user list for an inactive room.', {
							responseRoomId: chatMsgWrapper.RoomId,
							activeRoomId: currentRoomIdRef.current,
						});
						return;
					}

					const chatUsers = chatMsgWrapper.Data as ChatUser[];
					setCurrentRoomUsers(chatUsers);
					const roomId = currentRoomIdRef.current;
					if (roomId) {
						setRoomUsersByRoomId((prev) => ({
							...prev,
							[roomId]: chatUsers,
						}));
					}
				} else if (chatMsgWrapper.BroadcastType === ChatBroadcastType.RoomEvent) {
					const roomEvent = chatMsgWrapper.Data as ChatRoomEvent;
					const activeRoomId = currentRoomIdRef.current;
					if (
						roomEvent.EventType === ChatSignalREventType.MessageToRoom &&
						(roomEvent.ChatRoomId !== activeRoomId || activeRoomId === null)
					) {
						setRoomsState((prev) => ({
							...prev,
							[roomEvent.ChatRoomId]: { hasMoreMessages: true },
						}));
						dispatchChatActivityEvent({ action: 'set-room-activity', chatRoomId: roomEvent.ChatRoomId });

						if (!loadedChatRooms?.some((room) => room.Id === roomEvent.ChatRoomId)) {
							clearAvailableChatRoomsCache();
						}
					}
				}
			} catch (error) {
				console.error('Error parsing chat message:', error);
			}
		};

		subscribe('HubGeneric', 'CHAT', null, handleChatMessage);

		return () => {
			unsubscribe('HubGeneric', 'CHAT', null);
		};
	}, [
		subscribe,
		unsubscribe,
		user,
		loadedChatRooms,
		clearAvailableChatRoomsCache,
		clearJoinRetry,
		dispatchChatActivityEvent,
		sendMessagesDelivered,
	]);

	useEffect(() => {
		if (connectionState !== 'CONNECTED') {
			clearJoinRetry();
			pendingJoinRoomIdRef.current = null;
			if (connectionState === 'DISCONNECTED') {
				if (pendingLeaveRoomIdsRef.current.size > 0) {
					console.info('[Chat SignalR] Discarding pending room leaves after a full disconnect.', {
						roomIds: [...pendingLeaveRoomIdsRef.current],
					});
				}
				pendingLeaveRoomIdsRef.current.clear();
			}
			setCurrentRoomUsers([]);
			if (currentRoomId !== null) {
				setRoomHistoryLoaded(false);
				setIsResynchronizing(true);
			}
			return;
		}

		const pendingLeaveRoomIds = [...pendingLeaveRoomIdsRef.current];
		pendingLeaveRoomIdsRef.current.clear();
		if (pendingLeaveRoomIds.length > 0) {
			console.info('[Chat SignalR] Flushing pending room leaves before synchronization.', {
				roomIds: pendingLeaveRoomIds,
			});
		}
		pendingLeaveRoomIds.forEach((roomId) => {
			sendToHub({
				EventType: ChatSignalREventType.LeaveRoom,
				RoomId: roomId,
				Message: '',
			});
		});

		if (currentRoomId === null) {
			clearJoinRetry();
			return;
		}

		setRoomHistoryLoaded(false);
		setIsResynchronizing(true);
		pendingJoinRoomIdRef.current = currentRoomId;
		joinRetryAttemptRef.current = 0;
		let cancelled = false;

		const sendJoinAttempt = () => {
			if (
				cancelled ||
				connectionStateRef.current !== 'CONNECTED' ||
				currentRoomIdRef.current !== currentRoomId ||
				pendingJoinRoomIdRef.current !== currentRoomId
			) {
				return;
			}

			const attempt = joinRetryAttemptRef.current + 1;
			joinRetryAttemptRef.current = attempt;
			console.info('[Chat SignalR] Sending room synchronization request.', {
				roomId: currentRoomId,
				attempt,
			});
			sendToHub({
				EventType: ChatSignalREventType.JoinRoom,
				RoomId: currentRoomId,
				Message: '',
			});

			const retryDelay = JOIN_RETRY_DELAYS_MS[Math.min(attempt - 1, JOIN_RETRY_DELAYS_MS.length - 1)];
			joinRetryTimerRef.current = window.setTimeout(sendJoinAttempt, retryDelay);
		};

		sendJoinAttempt();

		return () => {
			cancelled = true;
			clearJoinRetry();
		};
	}, [clearJoinRetry, connectionState, currentRoomId, sendToHub]);

	const sendChatMessage = useCallback(
		(roomId: number, message: string, linkedObjectType?: number, linkedObjectId?: number) => {
			if (
				connectionState !== 'CONNECTED' ||
				!roomHistoryLoaded ||
				isResynchronizing ||
				currentRoomIdRef.current !== roomId
			) {
				return false;
			}

			sendToHub({
				EventType: ChatSignalREventType.MessageToRoom,
				RoomId: roomId,
				Message: message,
				LinkedObjectType: linkedObjectType,
				LinkedObjectId: linkedObjectId,
			});
			return true;
		},
		[connectionState, roomHistoryLoaded, isResynchronizing, sendToHub]
	);

	const joinRoom = useCallback(
		(roomId: number) => {
			if (currentRoomIdRef.current === roomId) return;

			clearJoinRetry();
			pendingJoinRoomIdRef.current = null;
			currentRoomIdRef.current = roomId;
			setCurrentRoomId(roomId);
			setMessages([]);
			setCurrentRoomUsers([]);
			setRoomHistoryLoaded(false);
			setIsResynchronizing(true);
			setRoomsState((prev) => ({ ...prev, [roomId]: { hasMoreMessages: false } }));
			dispatchChatActivityEvent({ action: 'current-room-changed', chatRoomId: roomId });
			dispatchChatActivityEvent({ action: 'clear-room-activity', chatRoomId: roomId });
		},
		[clearJoinRetry, dispatchChatActivityEvent]
	);

	const leaveRoom = useCallback(
		(roomId: number) => {
			if (connectionStateRef.current === 'CONNECTED') {
				pendingLeaveRoomIdsRef.current.delete(roomId);
				sendToHub({
					EventType: ChatSignalREventType.LeaveRoom,
					RoomId: roomId,
					Message: '',
				});
			} else if (connectionStateRef.current === 'RECONNECTING') {
				pendingLeaveRoomIdsRef.current.add(roomId);
				console.info('[Chat SignalR] Queued room leave during native reconnect.', { roomId });
			}

			if (currentRoomIdRef.current === roomId) {
				clearJoinRetry();
				pendingJoinRoomIdRef.current = null;
				currentRoomIdRef.current = null;
				setCurrentRoomId(null);
				setMessages([]);
				setCurrentRoomUsers([]);
				setRoomHistoryLoaded(false);
				setIsResynchronizing(false);
				dispatchChatActivityEvent({ action: 'current-room-changed', chatRoomId: null });
			}
		},
		[clearJoinRetry, sendToHub, dispatchChatActivityEvent]
	);

	return {
		connectionState,
		isConnected,
		isRoomReady,
		isResynchronizing,
		messages,
		currentRoomUsers,
		currentRoomId,
		roomUsersByRoomId,
		roomsState,
		sendChatMessage,
		joinRoom,
		leaveRoom,
	};
};
